<template>
  <ListItem
    :link="{ path: service.uri }"
    :query="itemQuery"
    :images="serviceImages"
    lateral-images-mobile
    is-external
    class="service-card"
  >
    <template #item-tags>
      <div class="service-card__badges">
        <Badge
          v-if="spa && showSpaDetails && spa.score > 0"
          class="score-badge"
        >
          <ScoreLeafIcon :size="20" />
          <span>{{ spa.score.toFixed(1) }}</span>
        </Badge>
      </div>
    </template>
    <template #item-tags-bottom>
      <Badge
        v-if="isDisplayedClosedByTemp"
        class="service-card__badges__badge"
        color="acent"
      >
        <div class="padded">
          <Lang
            by-key="enjoy-once-is-open"
            capitalize-first
          />
        </div>
      </Badge>


      <Badge
        v-else-if="isBlackFridaySpecialDisplayed"
        color="black-friday"
        class="service-card__badges__badge"
      >
        <div class="service-card__badges__badge__special">
          <GiftIcon class="service-card__badges__badge__special__icon" />
          <div>
            <span class="only-desktop-block">Black Friday</span>
            <span class="only-mobile">Black Friday</span>
          </div>
        </div>
      </Badge>

      <Badge
        v-else-if="isChristmasSpecialDisplayed"
        color="christmas"
        class="service-card__badges__badge"
      >
        <div class="service-card__badges__badge__special">
          <GiftIcon class="service-card__badges__badge__special__icon" />
          <div>
            <Lang
              class="only-desktop-block"
              capitalize-all
              by-key="christmas-special"
            />
            <Lang
              class="only-mobile"
              capitalize-all
              by-key="christmas"
            />
          </div>
        </div>
      </Badge>

      <Badge
        v-else-if="isChristmasCoupleDisplayed"
        color="christmas"
        class="service-card__badges__badge"
      >
        <div class="service-card__badges__badge__special">
          <GiftIcon class="service-card__badges__badge__special__icon" />
          <div>
            <Lang
              class="only-desktop-block"
              capitalize-all
              by-key="christmas-and-couple"
            />
            <Lang
              class="only-mobile"
              capitalize-all
              by-key="christmas"
            />
          </div>
        </div>
      </Badge>

      <Badge
        v-else-if="isChristmasFoodDisplayed"
        color="christmas"
        class="service-card__badges__badge"
      >
        <div class="service-card__badges__badge__special">
          <GiftIcon class="service-card__badges__badge__special__icon" />
          <div>
            <Lang
              class="only-desktop-block"
              capitalize-all
              by-key="christmas-and-food"
            />
            <Lang
              class="only-mobile"
              capitalize-all
              by-key="christmas"
            />
          </div>
        </div>
      </Badge>

      <Badge
        v-else-if="isValentinesSpecialDisplayed"
        color="valentine"
        class="service-card__badges__badge"
      >
        <div class="service-card__badges__badge__special">
          <span class="service-card__badges__badge__special__icon">❤</span>
          <div>
            <Lang
              class="only-desktop-block"
              capitalize-all
              by-key="valentines-special"
            />
            <Lang
              class="only-mobile"
              capitalize-all
              by-key="valentines"
            />
          </div>
        </div>
      </Badge>

      <Badge
        v-else-if="isFathersDayDisplayed"
        color="acent"
        class="service-card__badges__badge"
      >
        <div>
          <span class="only-desktop-block"><Lang by-key="fathers-day" /> ❤️</span>
          <span class="only-mobile"><Lang by-key="fathers-day" /></span>
        </div>
      </Badge>

      <Badge
        v-else-if="isMothersDayDisplayed"
        color="acent"
        class="service-card__badges__badge"
      >
        <div>
          <span class="only-desktop-block"><Lang by-key="mothers-day" /> ❤️</span>
          <span class="only-mobile"><Lang by-key="mothers-day" /></span>
        </div>
      </Badge>

      <Badge
        v-else-if="areBookingsEnabled"
        color="acent"
        class="service-card__badges__badge"
      >
        <CalendarIcon
          variant="dark"
          class="badge-icon"
        />

        <div class="only-mobile">
          <Lang
            by-key="booking"
            capitalize-first
          />
        </div>
        <div class="only-desktop-block">
          <Lang
            by-key="online-booking"
            capitalize-all
          />
        </div>
      </Badge>

      <Badge
        v-else-if="isSpecialCouplesBadgeDisplayed"
        color="acent"
        class="service-card__badges__badge"
      >
        <div class="only-mobile">
          <Lang
            by-key="couple"
            capitalize-all
            plural
          />
        </div>
        <div class="only-desktop-block">
          <Lang
            by-key="couple-special"
            capitalize-all
            plural
          />
        </div>
      </Badge>
    </template>

    <template #body>
      <div>
        <div class="service-card__header">
          <h3 class="service-card__header__service-name">
            {{ serviceName }}
          </h3>

          <p
            v-if="showSpaDetails && spa"
            class="service-card__header__spa-name"
          >
            en {{ spa.name }}
          </p>

          <div v-if="showSpaDetails && spa">
            <LocationLink
              :location="spa.location"
              show-icon
            />
          </div>
        </div>


        <hr
          class="service-card__hr"
          :class="{'with-offer': hasDiscount}"
        >

        <!-- Service: duración y precios -->
        <div class="service-card__info">
          <div
            v-if="service.durationMin"
            class="service-card__info__duration"
          >
            <ClockFilledIcon
              variant="dark"
              class="service-card__info__icon"
              :size="18"
            />
            <span>{{ service.durationMin }}'</span>
          </div>

          <div
            v-if="isIndividual && !hasSelectedPax"
            class="service-card__info__price"
          >
            <PersonIcon
              class="service-card__info__icon"
              :size="18"
            />
            <span
              v-if="hasDiscount"
              class="base-price-if-offer"
            >{{ truncatedPriceStr }}</span>
            <span :class="{'with-offer': hasDiscount}">{{ truncatedPriceWithDiscountStr }}</span>
          </div>
          <div
            v-if="!hasSelectedPax"
            class="service-card__info__couple-price"
          >
            <PersonCoupleIcon
              class="service-card__info__icon"
              :size="18"
            />
            <span
              v-if="hasDiscount"
              class="base-price-if-offer"
            >{{ truncatedCoupplePriceStr }}</span>
            <span :class="{'with-offer': hasDiscount}">{{ truncatedCoupplePriceWithDiscountStr }}</span>
          </div>

          <div
            v-if="hasSelectedPax"
            class="service-card__info__paxed-price"
          >
            <span class="multiplier">{{ selectedPax.value }}</span>
            <span class="multiplier">x</span>
            <PersonIcon
              class="service-card__info__icon"
              :size="18"
            />
            <span
              v-if="hasDiscount"
              class="base-price-if-offer"
            >{{ truncatedPaxedPriceStr }}</span>
            <span :class="{'with-offer': hasDiscount}">{{ truncatedPaxedPriceWithDiscountStr }}</span>
          </div>
        </div>

        <!-- Etiquetas en verde -->
        <div class="service-card__categories">
          <Badge
            v-for="tag in categoryTagsLimited"
            :key="tag.uuid"
            color="secondary"
            class="service-card__categories__tag"
          >
            <Lang
              :by-raw="tag.texts"
              key-for-raw="title"
            />
          </Badge>
        </div>

        <div
          v-if="service.durationMin && service.serviceTags.length"
          class="service-card__includes"
        >
          <Lang
            by-key="included-in-XX-min-service"
            capitalize-first
            :params="{time: service.durationMin}"
          />
        </div>

        <div
          v-else-if="service.serviceTags.length"
          class="service-card__includes"
        >
          <Lang
            by-key="included-in-service"
            capitalize-first
          />:
        </div>

        <div class="only-desktop-block">
          <div class="service-card__service-tags">
            <CheckListItem
              v-for="tag in serviceTagsLimited"
              :key="tag.uuid"
              reversed
            >
              <Lang
                :by-raw="tag.texts"
                key-for-raw="title"
              />
            </CheckListItem>
          </div>
        </div>

        <div class="only-mobile">
          <div class="service-card__service-tags">
            <CheckListItem
              v-for="tag in serviceTagsFirst"
              :key="tag.uuid"
              reversed
            >
              <Lang
                :by-raw="tag.texts"
                key-for-raw="title"
              />
            </CheckListItem>
          </div>
        </div>
      </div>
    </template>
  </ListItem>
</template>

<script lang="ts">
import ListItem from '../../shared/ListItem.vue'
import LeafScoreMeterAtom from '../../spa/atom/LeafScoreMeter.atom.vue'
import LocationLink from '../../shared/LocationLink.vue'
import Badge from '../../shared/Badge.vue'
import CheckListItem from '../../shared/CheckListItem.vue'
import ArrowIcon from '../../shared/icons/Arrow.icon.vue'
import ClockFilledIcon from '../../shared/icons/ClockFilled.icon.vue'
import PersonIcon from '../../shared/icons/Person.icon.vue'
import PersonCoupleIcon from '../../shared/icons/PersonCouple.icon.vue'
import CalendarIcon from '../../shared/icons/Calendar.icon.vue'
import ScoreLeafIcon from '../../shared/icons/ScoreLeaf.icon.vue'
import GiftIcon from '../../shared/icons/Gift.icon.vue'

import { Service } from '~/core/ts/entity/Service'
import { Spa } from '~/core/ts/entity/Spa'

import { translate } from '@/core/ts/util/translate'
import config from '@/core/ts/server-config'
import { Money } from '~/core/ts/entity/Money'
import { Promotion } from '~/core/ts/entity/Promotion'

export default defineNuxtComponent({
  components: {
    ListItem,
    LeafScoreMeterAtom,
    LocationLink,
    Badge,
    CheckListItem,
    ArrowIcon,
    ClockFilledIcon,
    PersonIcon,
    PersonCoupleIcon,
    CalendarIcon,
    ScoreLeafIcon,
    GiftIcon,
  },
  props: {
    service: {
      type: Object as PropType<Service>,
      required: true,
    },
    spa: {
      type: Object as PropType<Spa>,
        default: () => null,
    },
    showSpaDetails: {
      type: Boolean,
      default: () => false,
    },
    selectedPax: {
      type: Object as PropType<{ label: string, value: string | null }>,
      required: true,
    },
    hasSelectedPax: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    // Complex vars
    const categoryTags = (() => {
      let resultTags = [
        ...props.service.categoryTags,
        ...props.service.promotionTags,
      ]

      // Quitamos de la lista la etiqueta del día del padre y la madre.
      resultTags = Promotion.filterByNoFest(resultTags)

      return resultTags
    })()
    const categoryTagsLimited = categoryTags.slice(0, 3)

    const serviceTagsLimited = (() => {
      if (categoryTagsLimited.length > 1) {
        return props.service.serviceTags.slice(0, 1)
      }
      return props.service.serviceTags.slice(0, 3)
    })()
    const serviceTagsFirst = (() => {
      if (serviceTagsLimited.length) {
        return [serviceTagsLimited[0]]
      }

      return []
    })()

    // Smol vars
    const itemQuery = computed(() => {
      if (props.hasSelectedPax) {
        const query: Record<string, string | null> = {}
        query[props.selectedPax.label] = props.selectedPax.value
        return query
      }

      return {}
    })

    const serviceImages = props.service.images.slice(0, 1)

    const isIndividual = Service.isIndividual(props.service)

    const hasDiscount = computed(() => Service.hasAValidOffer(props.service, new Date()))

    const truncatedPriceStr = Money.toString(Service.getBasePrice(props.service), 0)
    const truncatedPriceWithDiscountStr = (() => {
      const price = Service.getDiscountedPrice(props.service, new Date())
      return Money.toString(price, 0)
    })()

    const truncatedCoupplePriceStr = (() => {
      if (isIndividual) {
        const price = Service.getBasePrice(props.service, 2)
        return Money.toString(price, 0)
      }

      const price = Service.getBasePrice(props.service)
      return Money.toString(price, 0)
    })()

    const truncatedCoupplePriceWithDiscountStr = (() => {
      if (isIndividual) {
        const price = Service.getDiscountedPrice(props.service, new Date(), 2)
        return Money.toString(price, 0)
      }

      const price = Service.getDiscountedPrice(props.service, new Date())
      return Money.toString(price, 0)
    })()

    const truncatedPaxedPriceStr = computed(() => {
      if (!props.hasSelectedPax) {
        const price = Service.getBasePrice(props.service)
        return Money.toString(price, 0)
      }

      const paxNumeric = Number(props.selectedPax.value)
      const price = Service.getBasePrice(props.service, paxNumeric)
      return Money.toString(price, 0)
    })

    const truncatedPaxedPriceWithDiscountStr = computed(() => {
      if (!props.hasSelectedPax) {
        return Service.getDiscountedPrice(props.service, new Date())
      }

      const paxNumeric = Number(props.selectedPax.value)
      return Money.toString(Service.getDiscountedPrice(props.service, new Date(), paxNumeric), 0)
    })

    // Status vars
    const isDisplayedClosedByTemp = (() => {
      if (props.service.status === 'disable_by_closed_temp_spa') {
        return true
      }

      if (!props.spa) {
        return false
      }

      return props.spa.status === 'closedtemp'
    })()

    const areBookingsEnabled = Service.hasBookingEnabled(props.service)

    const isSpecialCouplesBadgeDisplayed = (() => {
      for (const promo of props.service.promotionTags) {
        if (Promotion.isCouple(promo)) {
          return true
        }
      }

      return false
    })()

    const isBlackFridaySpecialDisplayed = (() => {
      if (!config.promos.isBlackFriday) {
        return false
      }

      for (const promo of props.service.promotionTags) {
        if (Promotion.getBlackFriday().uuid === promo.uuid) {
          return true
        }
      }

      return false
    })()

    const isChristmasSpecialDisplayed = (() => {
      if (!config.promos.isChristmas) {
        return false
      }

      for (const promo of props.service.promotionTags) {
        if (Promotion.isChristmas(promo)) {
          return true
        }
      }

      return false
    })()

    const isChristmasCoupleDisplayed = (() => {
      if (!config.promos.isChristmas) {
        return false
      }

      for (const promo of props.service.promotionTags) {
        if (Promotion.isCouple(promo)) {
          return true
        }
      }

      return false
    })()

    const isChristmasFoodDisplayed = (() => {
      if (!config.promos.isChristmas) {
        return false
      }

      const foodUUID = Promotion.getSpaAndFood().uuid
      for (const promo of props.service.promotionTags) {
        if (foodUUID === promo.uuid) {
          return true
        }
      }

      return false
    })()

    const isValentinesSpecialDisplayed = (() => {
      if (!config.promos.isValentines) {
        return false
      }

      for (const promo of props.service.promotionTags) {
        if (Promotion.isValentines(promo)) {
          return true
        }
      }

      return false
    })()

    const isFathersDayDisplayed = (() => {
      if (!config.promos.isFathersDay) {
        return false
      }

      for (const promo of props.service.promotionTags) {
        if (Promotion.isFathersDay(promo)) {
          return true
        }
      }

      return false
    })()

    const isMothersDayDisplayed = (() => {
      if (!config.promos.isMothersDay) {
        return false
      }

      for (const promo of props.service.promotionTags) {
        if (Promotion.isMothersDay(promo)) {
          return true
        }
      }

      return false
    })()

    // Textos
    const serviceName = translate(props.service.texts).name

    return {
      // Service data
      serviceImages,
      itemQuery,

      // Textos
      serviceName,

      // Medallas de categorías
      categoryTags,
      categoryTagsLimited,
      serviceTagsLimited,
      serviceTagsFirst,

      isIndividual,

      // Precios
      hasDiscount,
      truncatedPriceStr,
      truncatedPriceWithDiscountStr,
      truncatedCoupplePriceStr,
      truncatedCoupplePriceWithDiscountStr,
      truncatedPaxedPriceStr,
      truncatedPaxedPriceWithDiscountStr,

      // Status vars
      isDisplayedClosedByTemp,
      areBookingsEnabled,
      isSpecialCouplesBadgeDisplayed,
      isBlackFridaySpecialDisplayed,
      isChristmasSpecialDisplayed,
      isChristmasCoupleDisplayed,
      isChristmasFoodDisplayed,
      isValentinesSpecialDisplayed,

      isFathersDayDisplayed,
      isMothersDayDisplayed,
    }
  }
})
</script>

<style lang="scss" scoped>
.score-badge {
  @apply flex flex-row;
  @apply font-bold;
  @apply justify-center items-center;
}
.service-card {
  @apply h-full;

  &__header {
    @apply w-full;
    @apply py-2 px-2;

    &__service-name {
      @apply text-spl-dark text-lg;
      @apply font-semibold;
      @apply mb-2;

      @screen lg {
        @apply text-xl;
      }
    }

    &__spa-name {
      font-size: 14px;
      @apply text-spl-dark;
    }
  }

  &__badges {
    @apply flex flex-col flex-wrap;
    @apply items-end;

    @screen lg {
      @apply flex flex-row-reverse;

    }

    &__badge {
      @apply flex;
      @apply items-center;

      span.padded {
        @apply px-2 pt-1;
      }

      .badge-icon {
        @apply mr-1;
      }

      &__special {
        @apply flex;

        &__icon {
          @apply mt-2.5p mr-5p;
        }
      }

      @screen sm {
        @apply left-40p;
      }
    }
  }

  &__hr {
    @apply mt-2 mb-1;
    @apply border-spl-gray-1;

    @screen lg {
      @apply mb-2;
    }
  }

  &__hr.with-offer {
    @apply mb-4;
    @screen lg {
      @apply mb-4;
    }
  }

  &__info {
    @apply flex justify-end;
    @apply text-spl-dark;

    @apply font-semibold;

    .base-price-if-offer {
      @apply text-base;
      @apply text-spl-gray-3;
      @apply line-through;

      @apply absolute;

      @apply -top-4;
      @apply right-3;
    }

    &__duration, &__price, &__couple-price, &__paxed-price {
      @apply text-xl;
      @apply flex items-center;

      @apply px-2;
      @apply py-2;

      @screen md {
        @apply px-4;
      }
    }

    &__duration, &__price, &__couple-price, &__paxed-price {
      @media (max-width: 340px) {
        @apply text-lg;
      }
    }

    &__price, &__couple-price, &__paxed-price {
      @apply relative;
    }

    &__paxed-price {
      .multiplier {
        @apply text-xl;
        @apply pr-2;
      }
    }

    &__price, &__duration {
      @apply border-r-1;
    }

    &__icon {
      @apply mr-2;
      @media (max-width: 380px) {
        @apply mr-1;
      }
    }

    // .with-offer {
    //   @apply text-spl-primary-dark;
    // }
  }

  &__categories {
    @apply flex justify-end flex-wrap;
    @apply mt-4 mb-4;

    &__tag {
      @apply mr-1 mb-1;
    }
  }

  &__includes {
    @apply text-right;
    @apply font-bold;
    @apply mt-2;
    @apply mr-1;

    @media (max-width: 380px) {
      font-size: 11px;
    }
  }

  &__service-tags {
    @apply flex flex-col;

    @apply mx-2;
    @screen md {
      @apply mx-4;
    }

    @apply capitalize;
  }
}
</style>
